/**
 * exercise-row-trainer.js
 */

import React from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import { handlerExerciseCompletionStatus, supervisorExerciseCompletionStatus } from 'utils/record-status';
import { UserContext } from 'contexts/user/user-context';
import RecordMenuContext from 'contexts/record-context-menu/record-menu.context';
import Icon from 'components/icon';
import IconPatrolDetection from 'components/icon-patrol-detection';
import CompletionStatusIcon from 'components/record-status-icons/completion';
import {
  getOdorSummary,
  getPatrolSummary,
} from '../helpers';
import Row from '../row/row';
import RowStatusChip from '../row/row-status-chip';
import RowStatusTooltip from '../row/row-status-tooltip';


const useStyles = makeStyles(theme => ({
  completionChip: {
    minWidth: 120,
  },
}));


export default function ExerciseRowTrainer(props) {
  const {
    id,
    exercise,
    event,
    basePath,
    isSelected,
    onSelectionChange,
    workMode,
    workModeName,
    onViewReport,
  } = props;
  const classes = useStyles();
  const { user } = React.useContext(UserContext);
  const recordContextMenu = React.useContext(RecordMenuContext);
  const { t } = useTranslation('general');
  const isReportDisabled = exercise.userStats.reportableAnswerIds.length === 0;
  const reportDisabledReason = exercise.userStats.reportableAnswerIds.length === 0
    ? <>Reports can only be created if there are<br />handlers who have printable records<br />for this exercise.</>
    : '';
  const completionStatusParams = React.useMemo(() => {
    return supervisorExerciseCompletionStatus(
      exercise.userStats.complete,
      exercise.userStats.incomplete,
      event.date
    );
  }, [exercise.userStats.complete, exercise.userStats.incomplete, event.date]);
  const customCompare = `${exercise.reviewStats.notReviewed}-${exercise.reviewStats.accepted}-${exercise.reviewStats.rejected}`;


  const to = makeLocation(
    basePath,
    'training',
    'viewExercise',
    { exerciseId: id }
  );
  const routeLink = <RouterLink to={to} className='absolute-fill record-link' />;


  // status tooltip
  const statusTooltipItems = exercise.managedHandlers?.map(handler => {
    const params = handlerExerciseCompletionStatus(
      handler.userStats.complete,
      handler.userStats.incomplete,
      0, // trainers don't care about outdated
      event,
      handler.id,
      handler.answers.length,
      true
    );
    return {
      id: handler.id,
      icon: <Icon type={params.id} className={`icon-${params.id}`} />,
      name: handler.name,
      status: params.label
    };
  });


  // menu items
  const menuItems = [
    // view
    {
      title: 'View',
      iconType: 'view',
      component: RouterLink,
      to: makeLocation(basePath, 'training', 'viewExercise', { exerciseId: id }, null),
      isDefaultItem: true
    },

    // edit
    {
      title: 'Edit',
      iconType: 'edit',
      component: RouterLink,
      to: makeLocation(basePath, 'training', 'editExercise', { exerciseId: id }, null),
      disabled: !user.role.status.isActive || !exercise.canEdit,
      disabledReason: !user.role.status.isActive ? t('readOnlyAccount') : 'You do not have permission to edit this exercise'
    },

    // view report
    {
      title: 'View Report',
      iconType: 'report',
      disabled: isReportDisabled,
      disabledReason: reportDisabledReason,
      submenuItems: [{
        title: 'All Handlers',
        iconType: 'group',
        onSelect: () => onViewReport(Types.RecordGroup.TRAINING, exercise.userStats.reportableAnswerIds),
        tooltip: exercise.userStats.outdated > 0
          ? `One or more handlers have outdated records which will not be included in the report`
          : ''
      }, ...(exercise.managedHandlers
        // filter out not-attending
        ?.filter(handler => {
          const member = event.members.find(m => m.id === handler.id);
          return member && (!member.isOptional || member.isAttending || member.isAttending === null);
        })
        ?.map(handler => ({
          title: handler.name,
          iconType: 'handler',
          onSelect: () => onViewReport(Types.RecordGroup.TRAINING, handler.userStats.reportableAnswerIds),
          disabled: handler.userStats.reportableAnswerIds.length === 0,
          disabledReason: handler.userStats.reportableAnswerIds.length === 0
            ? `${handler.name} has no printable records`
            : '',
          tooltip: handler.userStats.outdated > 0
            ? `${handler.name} has ${handler.userStats.outdated} outdated record${handler.userStats.outdated === 1 ? '' : 's'} which will not be included in the report`
            : ''
        })) || [])]
    },

    // move to event
    {
      title: 'Move to Event ...',
      iconType: 'transfer',
      onSelect: () => recordContextMenu.moveToEventAction(id, exercise.eventId),
      disabled: !user.role.status.isActive || !event.canEdit,
      disabledReason: !user.role.status.isActive ? t('readOnlyAccount') : 'You do not have permission to move this exercise'
    },

    // duplicate submenu
    {
      title: 'Duplicate ...',
      iconType: 'copy',
      onSelect: () => recordContextMenu.duplicateExercise(id),
      disabled: !user.role.status.isActive || !event.canEdit,
      disabledReason: !user.role.status.isActive ? t('readOnlyAccount') : 'You do not have permission to edit the containing event'
    },

    // delete exercise
    {
      title: 'Delete ...',
      iconType: 'delete',
      onSelect: () => recordContextMenu.deleteExercise(id),
      disabled: !user.role.status.isActive || !exercise.canEdit || exercise.fullStats.complete > 0,
      disabledReason: !user.role.status.isActive
        ? t('readOnlyAccount')
        : !exercise.canEdit
          ? 'You do not have permission to delete this exercise'
          : <>
            Exercises with completions cannot be deleted.
            <Box mt={0.5}>
              Members with completions:
              <ul style={{ listStyleType: 'none', marginTop: 0 }}>
                {exercise.applicableHandlerDogs
                  .filter(handler => handler.hasCompletions)
                  .map(handler => <li key={handler.id}>{handler.name}</li>)}
              </ul>
            </Box>
          </>
    }
  ];


  return (
    <>
      {exercise &&
        <Row
          data-testid={id}
          data-testtype='record-exercise'
          recordType={Types.Record.EXERCISE}
          className='content'
          routeLink={routeLink}

          selection={{
            type: Types.RecordGroup.TRAINING,
            id,
            isSelected,
            isDisabled: isReportDisabled,
            disabledReason: reportDisabledReason,
            onChange: onSelectionChange
          }}

          icon={<IconPatrolDetection
            workMode={exercise.workMode}
            patrolType={workMode === Types.WorkMode.PATROL && exercise.patrol.patrolTypes.length === 1
              ? exercise.patrol.patrolTypes[0].type
              : Types.Patrol.SCENARIO}
          />}
          title={exercise.name}
          detail={`${workModeName}: ${workMode === Types.WorkMode.PATROL
            ? getPatrolSummary(Types.Record.EXERCISE, exercise.patrol.patrolTypes, t)
            : getOdorSummary(exercise.detection.environments)}`}

          optional={
            <div className='flex-hc record-status'>
              {/* completion chip - desktop */}
              <span className='status-desktop'>
                <RowStatusTooltip items={statusTooltipItems}>
                  <RowStatusChip
                    className={
                      `chip-${completionStatusParams.id}`
                      + ` chip-font-${completionStatusParams.id}`
                      + ` ${classes.completionChip}`}
                    label={<><Icon type={completionStatusParams.id} />{completionStatusParams.label}</>}
                    to={to}
                  />
                </RowStatusTooltip>
              </span>
              {/* completion icon - mobile */}
              <span className='status-mobile'>
                <CompletionStatusIcon
                  params={completionStatusParams}
                  to={to}
                />
              </span>
            </div>}

          menu={{
            ariaLabel: 'exercise menu options',
            className: '',
            items: menuItems
          }}

          customCompare={customCompare}
        />}
    </>
  )
}